import * as React from "react";
import { getOpinionArticleType } from "@foundation/opinion-article-utils";
import { OPINION_ARTICLE_TYPE } from "@foundation/opinion-article-utils/lib/types";

import type { Eyebrow, Person } from "~types/types";
import { getOpinionBybrowPrefix } from "~utils/opinionBybrow";

import styles from "./styles.module.css";

interface BybrowProps {
    relatedItem?: boolean;
    credits?: Person[];
}

export interface EyebrowProps {
    text: string | undefined;
    url?: string;
    relatedEyebrow?: boolean;
}

interface LinkedEyebrowProps {
    text: string;
    url: string;
}

interface BrowProps {
    eyebrow?: Eyebrow;
    text?: string;
    url?: string;
    relatedItem?: boolean;
    credits?: Person[];
}

export function getEyebrowAuthorsText(authors: Person[]) {
    return authors.reduce((acc, { name }, idx, arr) => {
        if (idx === 0) {
            return name;
        }

        if (idx === arr.length - 1) {
            return `${acc} & ${name}`;
        }
        return `${acc}, ${name}`;
    }, "");
}

export const getOpinionAuthorsText = (authors?: Person[]) => {
    if (!authors) {
        return undefined;
    }

    const opinionArticleType = getOpinionArticleType(authors);

    if (opinionArticleType === OPINION_ARTICLE_TYPE.EDITORIAL_BOARD) {
        return "Editorial Board";
    }

    return getEyebrowAuthorsText(authors);
};

export function Bybrow({ credits = [], relatedItem = false }: BybrowProps) {
    const type = relatedItem ? "related-item-bybrow" : "bybrow";
    const hidden = relatedItem ? "hidden" : "";
    const className = `font-bold text-sm mb-[5px] ${hidden}`;

    const opinionAuthorsText = getOpinionAuthorsText(credits);
    const bybrowPrefix = getOpinionBybrowPrefix(credits);

    return (
        <div className={className} data-component={type}>
            <span className={styles.bybrow}>{opinionAuthorsText}</span>
            {bybrowPrefix && (
                <span className="text-gray-600"> | {bybrowPrefix}</span>
            )}{" "}
        </div>
    );
}

function LinkedEyebrow({ text, url }: LinkedEyebrowProps) {
    return (
        <a href={url} className="hover:underline">
            {text}
        </a>
    );
}

export function EyebrowComponent({
    text,
    url = "",
    relatedEyebrow = false,
}: EyebrowProps) {
    if (!text) return null;

    const eyebrowType = relatedEyebrow ? "related-eyebrow" : "eyebrow";

    return (
        <div
            className="capitalize text-sm mb-[5px] capitalize"
            data-component={eyebrowType}
        >
            {url ? <LinkedEyebrow text={text} url={url} /> : text}
        </div>
    );
}

export default function Brow({
    eyebrow,
    credits,
    relatedItem = false,
}: BrowProps) {
    const { text, url } = eyebrow || {};

    if (!text && !credits) return null;

    if (eyebrow?.bybrowEligible && credits?.length) {
        return <Bybrow credits={credits} relatedItem={relatedItem} />;
    }

    return (
        <EyebrowComponent text={text} url={url} relatedEyebrow={relatedItem} />
    );
}
